import React, { useState, useRef, useEffect, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import moment from 'moment';

//Components
import { Button, Tooltip, Select } from "@gpstrackit/react-controls";
import { NotificationManager } from "Components/Notifications";
import InputDate from 'Components/InputDate';
import DataViewInfiniteScroll from 'Components/DataViewInfiniteScroll';
import ProgressBar from "Components/ProgressBar";
import Filters from 'Components/Filters';

//Icons
import SearchIcon from '@mui/icons-material/Search';
import { Icon, Chip } from '@mui/material';

//Helpers
import {
    getMinDateLimitBehaviour,
    getDateFormatted,
    formatAddress,
    getEventColor,
    getRssiSignalIcon,
    getHDopColorLabel,
    secondsToHours
} from 'Core/data/Helpers';
import { truncateText } from 'Utils/helpers';

//Views
import DeviceReadingInfo from 'Modules/rawData/.components/tab/.components/details';

//Actions
import {
    rawDataTabSetBeforeRangeDate,
    getDeviceReadingOpenModal,
    rawDataTabSetFilters,
    clearRedux
} from 'Redux/actions';

import { selectEntityState } from 'Core/common/redux/infiniteScrollSlice';
import { clientQuery } from 'Core/data/GraphQLApi';
import { endpoints } from 'Core/defaultValues';

//Styles
import '../tab.scss'

const RANGE_MODE_ROWS = 500;

const VidFleetView = (props) => {
    const dispatch = useDispatch();
    const { companyId, messages, height, className, timezone, valuesPerPage, device } = props;
    const tableRef = useRef();

    const { filters } = useSelector(state => state.rawDataTabRedux);
    const { openModal } = useSelector(state => state.deviceReadingRedux);
    const events = useSelector(state => state.generalRedux.events);
    const beforeRangeDateRed = useSelector(state => state.rawDataTabRedux.beforeRangeDate);
    const entityState = useSelector(selectEntityState('device-readings-vidfleet'));

    const [dateType, setDateType] = useState({ id: 'unitTime', label: messages['common_unit_time'] });
    const [dateFilters, setDateFilters] = useState(filters)
    const [columns, setColumns] = useState([
        {
            key: 'view',
            show: true,
            locked: true,
            title: "",
            width: 40,
            icon: true,
            render: (item) => {
                return (
                    <Tooltip title={messages['raw_data_tab_device_reading_info']}>
                        <Icon style={{ cursor: "pointer" }} className="toolbar-list-icon fs-20" onClick={() => {
                            dispatch(getDeviceReadingOpenModal(true, { id: item?.id, deviceId: item?.deviceId }))
                        }}>
                            remove_red_eye
                        </Icon>
                    </Tooltip>

                )
            }
        },
        {
            key: 'id',
            show: true,
            title: messages["raw_data_tab_id"],
            width: 200,
            field: "id",
            export: true,
            resize: true,
            functionString: 'return row?.id',
            code: "raw_data_tab_id",
        },
        {
            key: 'sequenceNumber',
            show: true,
            title: messages["raw_data_tab_sequenceNumber"],
            width: 200,
            field: "sequenceNumber",
            export: true,
            resize: true,
            functionString: 'return row?.sequenceNumber',
            code: "raw_data_tab_sequenceNumber",
        },
        {
            key: 'serverTime',
            show: true,
            title: messages["raw_data_tab_server_time"],
            width: 300,
            field: "serverTime",
            export: true,
            resize: true,
            functionString: `
                function formatDateWithTimezone(date, tz) {
                    let dateToformat = moment(date)
                    let formatted = moment(dateToformat).tz(tz).format("YYYY/MM/DD HH:mm:ss")
                    return formatted;
                }
                let timezone = '${timezone}'
                return row?.serverTime ? formatDateWithTimezone(row.serverTime, timezone) : ''
                `,
            code: "raw_data_tab_server_time",
            render: (dataItem) => {
                return (
                    <span>
                        {
                            dataItem.serverTime &&
                            getDateFormatted(dataItem.serverTime, "date", "datetime", null, timezone)
                        }
                    </span>
                )
            }
        },
        {
            key: 'unitTime',
            show: true,
            title: messages["raw_data_tab_unit_time"],
            width: 300,
            field: "unitTime",
            export: true,
            resize: true,
            functionString: `
                function formatDateWithTimezone(date, tz) {
                    let dateToformat = moment(date)
                    let formatted = moment(dateToformat).tz(tz).format("YYYY/MM/DD HH:mm:ss")
                    return formatted;
                }
                let timezone = '${timezone}'
                return row?.unitTime ? formatDateWithTimezone(row.unitTime, timezone) : ''
            `,
            code: "raw_data_tab_unit_time",
            render: (dataItem) => {
                return (
                    <span>
                        {
                            dataItem.unitTime &&
                            getDateFormatted(dataItem.unitTime, "date", "datetime", null, timezone)
                        }
                    </span>
                )
            }
        },
        {
            key: 'createdAt',
            show: true,
            title: messages["raw_data_tab_last_message_time"],
            width: 300,
            field: "createdAt",
            export: true,
            resize: true,
            functionString: `
                function formatDateWithTimezone(date, tz) {
                    let dateToformat = moment(date)
                    let formatted = moment(dateToformat).tz(tz).format("YYYY/MM/DD HH:mm:ss")
                    return formatted;
                }
                let timezone = '${timezone}'
                return row?.createdAt ? formatDateWithTimezone(row.createdAt, timezone) : ''
            `,
            code: "raw_data_tab_last_message_time",
            render: (dataItem) => {
                return (
                    <span>
                        {
                            dataItem.createdAt &&
                            getDateFormatted(dataItem.createdAt, "date", "datetime", null, timezone)
                        }
                    </span>
                )
            }
        },
        {
            key: 'latitude',
            show: true,
            title: messages["raw_data_tab_latitude"],
            width: 200,
            field: "latitude",
            export: true,
            resize: true,
            functionString: 'return row?.latitude',
            code: "raw_data_tab_latitude",
        },
        {
            key: 'longitude',
            show: true,
            title: messages["raw_data_tab_longitude"],
            width: 200,
            field: "longitude",
            export: true,
            resize: true,
            functionString: 'return row?.longitude',
            code: "raw_data_tab_longitude",
        },
        {
            key: 'driverName',
            show: true,
            title: messages["raw_data_tab_driver_name"],
            width: 250,
            field: "driverName",
            export: true,
            resize: true,
            functionString: 'return row?.driverName',
            code: "raw_data_tab_driver_name",
        },
        {
            key: 'eventName',
            show: true,
            title: messages["raw_data_tab_event"],
            width: 250,
            field: "eventName",
            export: true,
            resize: true,
            functionString: 'return row?.eventName',
            code: "raw_data_tab_event",
            render: (dataItem) => {
                let eventCode = dataItem?.eventCode
                let validateEvent = eventCode && eventCode?.includes("CameraEvent") || eventCode?.includes("AutoSnapshot")
                let deviceData = null
                try {
                    deviceData = JSON.parse(JSON.parse(dataItem.deviceData))
                } catch (error) { }
                return (
                    <>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            {
                                validateEvent &&
                                <span
                                    style={{ height: 20, width: 20, marginRight: 5 }}
                                    className="cursor-pointer pr-1"
                                    onClick={() => props.showMedia ? props.showMedia(deviceData) : {}}
                                >
                                    <SearchIcon />
                                </span>
                            }
                            {
                                dataItem.eventName &&
                                <span style={{ color: `${getEventColor(events, dataItem.eventName)}` }}>
                                    {dataItem.eventName}
                                </span>
                            }
                        </div>
                    </>
                )
            }
        },
        {
            key: 'eventCode',
            show: true,
            title: messages["raw_data_tab_event_code"],
            width: 150,
            field: "eventCode",
            export: true,
            resize: true,
            functionString: 'return row?.eventCode',
            code: "raw_data_tab_event_code",
        },
        {
            key: 'formattedAddress',
            show: true,
            title: messages["raw_data_tab_gps_event_address"],
            width: 350,
            field: "formattedAddress",
            export: true,
            resize: true,
            functionString: `
                function isValidJson(str) {
                    try { JSON.parse(str) } catch (e) { return false }
                    return true
                }
                function formatAddress(element) {
                    let newAddress = element?.formattedAddress || ''
                    if (!newAddress || newAddress == 'null') {
                        if (element?.address && isValidJson(element?.address)) {
                            const { formatted } = JSON.parse(element?.address)
                            if (formatted) { newAddress = formatted || '' }
                        } else {
                            if (element?.address && element?.address?.formatted) {
                                return element.address.formatted
                            }
                            newAddress = ''
                        }
                    }
                    return newAddress
                }
                return formatAddress(row) || 'N/A'
            `,
            code: "raw_data_tab_gps_event_address",
            render: (dataItem) => {
                return (
                    <span>{formatAddress(dataItem) || 'N/A'}</span>
                )
            }
        },
        {
            key: 'spd',
            show: true,
            title: messages["raw_data_tab_speed"],
            width: 150,
            field: "speed",
            export: true,
            resize: true,
            functionString: 'return row?.speed',
            code: "raw_data_tab_speed",
            render: (dataItem) => {
                return (
                    <span>{dataItem?.speed}</span>
                )
            }
        },
        {
            key: 'ecuSpeed',
            show: true,
            title: messages["raw_data_tab_ecu_speed"],
            width: 150,
            field: "ecuSpeed",
            export: true,
            resize: true,
            functionString: 'return row?.ecuSpeed',
            code: "raw_data_tab_ecu_speed",
            render: (dataItem) => {
                return (
                    <span>{dataItem?.ecuSpeed}</span>
                )
            }
        },
        {
            key: 'speedLimit',
            show: true,
            title: messages["raw_data_tab_speed_limit"],
            width: 150,
            field: "speedLimit",
            export: true,
            resize: true,
            functionString: 'return row?.speedLimit',
            code: "raw_data_tab_speed_limit",
            render: (dataItem) => {
                return (
                    <span>{dataItem?.speedLimit}</span>
                )
            }
        },
        {
            key: 'heading',
            show: true,
            title: messages["raw_data_tab_heading"],
            width: 150,
            field: "heading",
            export: true,
            resize: true,
            functionString: 'return row?.heading',
            code: "raw_data_tab_heading",
            render: (dataItem) => {
                return (
                    <span>{dataItem?.heading}</span>
                )
            }
        },
        {
            key: 'ignitionStatus',
            show: true,
            title: messages["raw_data_tab_ignition_status"],
            width: 150,
            field: "ignitionStatus",
            export: true,
            resize: true,
            functionString: 'return row?.ignitionStatus',
            code: "raw_data_tab_ignition_status",
            render: (dataItem) => {
                return (
                    <span>{dataItem?.ignitionStatus}</span>
                )
            }
        },
        {
            key: 'notificationType',
            show: true,
            title: messages["raw_data_tab_notification_type"],
            width: 150,
            export: true,
            resize: true,
            functionString: 'return row?.deviceData? JSON.parse(JSON.parse(row?.deviceData))?.notificationType : ""',
            code: "raw_data_tab_notification_type",
            render: (dataItem) => {
                let data = {}
                try {
                    data = dataItem?.deviceData ? JSON.parse(JSON.parse(dataItem?.deviceData)) : {}
                } catch (exc) {
                    console.log(`Formatting error in rowId ${dataItem?.id}: ${dataItem?.deviceData}`)
                }
                return (
                    <span>{data?.notificationType}</span>
                )
            }
        },
        {
            key: 'tripId',
            show: true,
            title: messages["raw_data_tab_trip_id"],
            width: 350,
            export: true,
            resize: true,
            functionString: 'return row?.deviceData? JSON.parse(JSON.parse(row?.deviceData))?.tripID : ""',
            code: "raw_data_tab_trip_id",
            render: (dataItem) => {
                let data = {}
                try {
                    data = dataItem?.deviceData ? JSON.parse(JSON.parse(dataItem?.deviceData)) : {}
                } catch (exc) {

                }
                return (
                    <span>{data?.tripID}</span>
                )
            }
        },
        {
            key: 'pictureID',
            show: true,
            title: messages["raw_data_tab_picture_id"],
            width: 280,
            export: true,
            resize: true,
            functionString: 'return row?.deviceData? JSON.parse(JSON.parse(row?.deviceData))?.pictureID : ""',
            code: "raw_data_tab_picture_id",
            render: (dataItem) => {
                let data = {}
                try {
                    data = dataItem?.deviceData ? JSON.parse(JSON.parse(dataItem?.deviceData)) : {}
                } catch (exc) {

                }
                return (
                    <span>{data?.pictureID}</span>
                )
            }
        },
        {
            key: 'eventCategory',
            show: true,
            title: messages["raw_data_tab_event_category"],
            width: 150,
            export: true,
            resize: true,
            functionString: 'return row?.deviceData? JSON.parse(JSON.parse(row?.deviceData))?.eventCategory : ""',
            code: "raw_data_tab_event_category",
            render: (dataItem) => {
                let data = {}
                try {
                    data = dataItem?.deviceData ? JSON.parse(JSON.parse(dataItem?.deviceData)) : {}
                } catch (exc) {

                }
                return (
                    <span>{data?.eventCategory}</span>
                )
            }
        },
        {
            key: 'eventLevel',
            show: true,
            title: messages["raw_data_tab_event_level"],
            width: 150,
            export: true,
            resize: true,
            functionString: 'return row?.deviceData? JSON.parse(JSON.parse(row?.deviceData))?.eventLevel : ""',
            code: "raw_data_tab_event_level",
            render: (dataItem) => {
                let data = {}
                try {
                    data = dataItem?.deviceData ? JSON.parse(JSON.parse(dataItem?.deviceData)) : {}
                } catch (exc) {

                }
                return (
                    <span>{data?.eventLevel}</span>
                )
            }
        },
        {
            key: 'cameraClipKey',
            show: true,
            title: messages["raw_data_tab_camera_clip_key"],
            width: 150,
            export: true,
            resize: true,
            functionString: 'return row?.deviceData? JSON.parse(JSON.parse(row?.deviceData))?.cameraClipKey : ""',
            code: "raw_data_tab_camera_clip_key",
            render: (dataItem) => {
                let data = {}
                try {
                    data = dataItem?.deviceData ? JSON.parse(JSON.parse(dataItem?.deviceData)) : {}
                } catch (exc) {

                }
                return (
                    <span>{data?.cameraClipKey}</span>
                )
            }
        },
        {
            key: 'cameraClipOffset',
            show: true,
            title: messages["raw_data_tab_camera_clip_offset"],
            width: 200,
            export: true,
            resize: true,
            functionString: 'return row?.deviceData? JSON.parse(JSON.parse(row?.deviceData))?.cameraClipOffset : ""',
            code: "raw_data_tab_camera_clip_offset",
            render: (dataItem) => {
                let data = {}
                try {
                    data = dataItem?.deviceData ? JSON.parse(JSON.parse(dataItem?.deviceData)) : {}
                } catch (exc) {

                }
                return (
                    <span>{data?.cameraClipOffset}</span>
                )
            }
        },
        {
            key: 'clipID',
            show: true,
            title: messages["raw_data_tab_clip_id"],
            width: 150,
            export: true,
            resize: true,
            functionString: 'return row?.deviceData? JSON.parse(JSON.parse(row?.deviceData))?.clipID : ""',
            code: "raw_data_tab_clip_id",
            render: (dataItem) => {
                let data = {}
                try {
                    data = dataItem?.deviceData ? JSON.parse(JSON.parse(dataItem?.deviceData)) : {}
                } catch (exc) {

                }
                return (
                    <span>{data?.clipID}</span>
                )
            }
        },
        {
            key: 'duration',
            show: true,
            title: messages["raw_data_tab_duration"],
            width: 150,
            export: true,
            resize: true,
            functionString: `
                function getDuration(deviceData) {
                    try {
                        return deviceData ? JSON.parse(JSON.parse(deviceData))?.duration : ''
                    } catch (exc) {
                        return ''
                    }
                }
                return getDuration(row?.deviceData)
            `,
            code: "raw_data_tab_duration",
            render: (dataItem) => {
                let data = {}
                try {
                    data = dataItem?.deviceData ? JSON.parse(JSON.parse(dataItem?.deviceData)) : {}
                } catch (exc) {

                }
                return (
                    <span>{data?.duration}</span>
                )
            }
        },
        {
            key: 'rotate',
            show: true,
            title: messages["raw_data_tab_rotate"],
            width: 150,
            export: true,
            resize: true,
            functionString: 'return row?.deviceData? JSON.parse(JSON.parse(row?.deviceData))?.rotate : ""',
            code: "raw_data_tab_rotate",
            render: (dataItem) => {
                let data = {}
                try {
                    data = dataItem?.deviceData ? JSON.parse(JSON.parse(dataItem?.deviceData)) : {}
                } catch (exc) {

                }
                return (
                    <span>{data?.rotate}</span>
                )
            }
        },
        {
            key: 'cameraStatus',
            show: true,
            title: messages["raw_data_tab_camera_status"],
            width: 150,
            export: true,
            resize: true,
            functionString: 'return row?.deviceData? JSON.parse(JSON.parse(row?.deviceData))?.cameraStatus : ""',
            code: "raw_data_tab_camera_status",
            render: (dataItem) => {
                let data = {}
                try {
                    data = dataItem?.deviceData ? JSON.parse(JSON.parse(dataItem?.deviceData)) : {}
                } catch (exc) {

                }
                return (
                    <span>{data?.cameraStatus}</span>
                )
            }
        },
        {
            key: 'idleStatus',
            show: true,
            title: messages["raw_data_tab_idle_status"],
            width: 150,
            export: true,
            resize: true,
            functionString: 'return row?.deviceData? JSON.parse(JSON.parse(row?.deviceData))?.idleStatus : ""',
            code: "raw_data_tab_idle_status",
            render: (dataItem) => {
                let data = {}
                try {
                    data = dataItem?.deviceData ? JSON.parse(JSON.parse(dataItem?.deviceData)) : {}
                } catch (exc) {

                }
                return (
                    <span>{data?.idleStatus}</span>
                )
            }
        },
        {
            key: 'SDCardStatus',
            show: true,
            title: messages["raw_data_tab_sdcard_status"],
            width: 150,
            export: true,
            resize: true,
            functionString: 'return row?.deviceData? JSON.parse(JSON.parse(row?.deviceData))?.sdCardStatus : ""',
            code: "raw_data_tab_sdcard_status",
            render: (dataItem) => {
                let data = {}
                try {
                    data = dataItem?.deviceData ? JSON.parse(JSON.parse(dataItem?.deviceData)) : {}
                } catch (exc) {

                }
                return (
                    <span>{data?.sdCardStatus}</span>
                )
            }
        },
        {
            key: 'dataUsageInKB',
            show: true,
            title: messages["raw_data_tab_data_usage_in_kb"],
            width: 150,
            export: true,
            resize: true,
            functionString: `
                function getDataUsageInKB(deviceData) {
                    try {
                        return deviceData ? (JSON.parse(JSON.parse(deviceData))?.dataUsageInKB || "") : ""
                    } catch (exc) {
                        return ""
                    }
                }
                return getDataUsageInKB(row?.deviceData)
            `,
            code: "raw_data_tab_data_usage_in_kb",
            render: (dataItem) => {
                let data = {}
                try {
                    data = dataItem?.deviceData ? JSON.parse(JSON.parse(dataItem?.deviceData)) : {}
                } catch (exc) {

                }
                return (
                    <span>{data?.dataUsageInKB || ""}</span>
                )
            }
        },
        {
            key: 'transactionID',
            show: true,
            title: messages["raw_data_tab_transaction_id"],
            width: 150,
            export: true,
            resize: true,
            functionString: 'return row?.deviceData? JSON.parse(JSON.parse(row?.deviceData))?.transactionID : ""',
            code: "raw_data_tab_transaction_id",
            render: (dataItem) => {
                let data = {}
                try {
                    data = dataItem?.deviceData ? JSON.parse(JSON.parse(dataItem?.deviceData)) : {}
                } catch (exc) {

                }
                return (
                    <span>{data?.transactionID}</span>
                )
            }
        },
        {
            key: 'status',
            show: true,
            title: messages["raw_data_tab_status"],
            width: 150,
            export: true,
            resize: true,
            functionString: 'return row?.deviceData? JSON.parse(JSON.parse(row?.deviceData))?.status : ""',
            code: "raw_data_tab_status",
            render: (dataItem) => {
                let data = {}
                try {
                    data = dataItem?.deviceData ? JSON.parse(JSON.parse(dataItem?.deviceData)) : {}
                } catch (exc) {

                }
                return (
                    <span>{data?.status}</span>
                )
            }
        },
        {
            key: 'errorMsg',
            show: true,
            title: messages["raw_data_tab_error_msg"],
            width: 150,
            export: true,
            resize: true,
            functionString: 'return row?.deviceData? JSON.parse(JSON.parse(row?.deviceData))?.errorMsg : ""',
            code: "raw_data_tab_error_msg",
            render: (dataItem) => {
                let data = {}
                try {
                    data = dataItem?.deviceData ? JSON.parse(JSON.parse(dataItem?.deviceData)) : {}
                } catch (exc) {

                }
                return (
                    <span>{data?.errorMsg}</span>
                )
            }
        },
        {
            key: 'validTimeRange',
            show: true,
            title: messages["raw_data_tab_valid_time_range"],
            width: 150,
            export: true,
            resize: true,
            functionString: 'return row?.deviceData? JSON.parse(JSON.parse(row?.deviceData))?.validTimeRange : ""',
            code: "raw_data_tab_valid_time_range",
            render: (dataItem) => {
                let data = {}
                try {
                    data = dataItem?.deviceData ? JSON.parse(JSON.parse(dataItem?.deviceData)) : {}
                } catch (exc) {

                }
                return (
                    <span>{data?.validTimeRange}</span>
                )
            }
        },
        {
            key: 'warning',
            show: true,
            title: messages["raw_data_tab_warning"],
            width: 150,
            export: true,
            resize: true,
            functionString: 'return row?.deviceData? JSON.parse(JSON.parse(row?.deviceData))?.warning : ""',
            code: "raw_data_tab_warning",
            render: (dataItem) => {
                let data = {}
                try {
                    data = dataItem?.deviceData ? JSON.parse(JSON.parse(dataItem?.deviceData)) : {}
                } catch (exc) {

                }
                return (
                    <span>{data?.warning}</span>
                )
            }
        },
        {
            key: 'distance',
            show: true,
            title: messages['raw_data_tab_distance'],
            width: 100,
            field: "distance",
            export: true,
            resize: true,
            functionString: 'return row?.distance',
            code: "raw_data_tab_distance",
        },
        {
            key: 'accumulatedDistance',
            show: true,
            title: messages['raw_data_tab_accumulated_distance'],
            width: 200,
            export: true,
            resize: true,
            functionString: 'return row?.deviceData ? JSON.parse(JSON.parse(row?.deviceData))?.accumulatedDistance || "" : ""',
            code: "raw_data_tab_accumulated_distance",
            render: (dataItem) => {
                let data = {}
                try {
                    data = dataItem?.deviceData ? JSON.parse(JSON.parse(dataItem?.deviceData)) : {}
                } catch (exc) {

                }
                return (
                    <span>{data?.accumulatedDistance || ''}</span>
                )
            }
        },
        {
            key: 'engineHours',
            show: true,
            title: messages['raw_data_tab_engine_hours'],
            width: 150,
            export: true,
            resize: true,
            functionString: `
                function secondsToHours(seconds){
                    if (seconds == null) return 0;
                    return seconds / 3600;
                }
                let rawHours = row?.engineHours ?? 0;
                let hours = secondsToHours(rawHours);
                return Math.floor(hours * 100) / 100;
            `,
            code: "raw_data_tab_engine_hours",
            field: "engineHours",
            render: (dataItem) => {
                let rawHours = dataItem?.engineHours ?? 0;
                let hours = secondsToHours(rawHours);
                let engineHours = Math.floor(hours * 100) / 100;
                return (
                    <span>{engineHours}</span>
                )
            }
        },
        {
            key: 'engineHours_off',
            show: true,
            title: messages['raw_data_tab_engineHours_off'],
            width: 200,
            export: true,
            resize: true,
            functionString: `
                function secondsToHours(seconds){
                    if (seconds == null) return 0;
                    return seconds / 3600;
                }
                let offsetEngineHours = ${device?.offsetEngineHours || 0};
                let rawHours = row?.engineHours ?? 0;
                let hours = secondsToHours(rawHours);
                return Math.floor((hours + offsetEngineHours) * 100) / 100;
            `,
            code: "raw_data_tab_engineHours_off",
            render: (dataItem) => {
                let rawHours = dataItem?.engineHours ?? 0;
                let hours = secondsToHours(rawHours); // Mantenemos el secondsToHours original de la UI
                let offsetEngineHours = device?.offsetEngineHours || 0;
                let value = Math.floor((hours + offsetEngineHours) * 100) / 100;
                return (
                    <span>{value}</span>
                )
            }
        },
        {
            key: 'ecuEngineHours',
            show: true,
            title: messages['raw_data_tab_ecu_engine_hours'],
            width: 200,
            export: true,
            resize: true,
            functionString: `
                let val = row?.ecuEngineHours ?? 0;
                return Math.floor(val * 100) / 100;
            `,
            code: "raw_data_tab_ecu_engine_hours",
            field: "ecuEngineHours",
            render: (dataItem) => {
                let val = dataItem?.ecuEngineHours ?? 0;
                let ecuEngineHours = Math.floor(val * 100) / 100;
                return (
                    <span>{ecuEngineHours}</span>
                )
            }
        },
        {
            key: 'ecuEngineHours_off',
            show: true,
            title: messages['raw_data_tab_ecu_engineHours_off'],
            width: 250,
            export: true,
            resize: true,
            functionString: `
                let offsetEcuEngineHours = ${device?.offsetEcuEngineHours || 0};
                let val = row?.ecuEngineHours ?? 0;
                return Math.floor((val + offsetEcuEngineHours) * 100) / 100;
            `,
            code: "raw_data_tab_ecu_engineHours_off",
            render: (dataItem) => {
                let offsetEcuEngineHours = device?.offsetEcuEngineHours || 0;
                let val = dataItem?.ecuEngineHours ?? 0;
                let value = Math.floor((val + offsetEcuEngineHours) * 100) / 100;
                return (
                    <span>{value}</span>
                )
            }
        },
        {
            key: 'engineOdometer',
            show: true,
            title: messages['raw_data_tab_engine_odometer'],
            width: 200,
            export: true,
            resize: true,
            functionString: `
                let val = row?.engineOdometer ?? 0;
                return Math.floor(val * 100) / 100;
            `,
            code: "raw_data_tab_engine_odometer",
            field: "engineOdometer",
            render: (dataItem) => {
                let val = dataItem?.engineOdometer ?? 0;
                let engineOdometer = Math.floor(val * 100) / 100;
                return (
                    <span>{engineOdometer}</span>
                )
            }
        },
        {
            key: 'engineOdometer_off',
            show: true,
            title: messages['raw_data_tab_engineOdometer_off'],
            width: 250,
            export: true,
            resize: true,
            functionString: `
                let offsetEngineOdometer = ${device?.offsetEngineOdometer || 0};
                let val = row?.engineOdometer ?? 0;
                return Math.floor((val + offsetEngineOdometer) * 100) / 100;
            `,
            code: "raw_data_tab_engineOdometer_off",
            render: (dataItem) => {
                let offsetEngineOdometer = device?.offsetEngineOdometer || 0;
                let val = dataItem?.engineOdometer ?? 0;
                let value = Math.floor((val + offsetEngineOdometer) * 100) / 100;
                return (
                    <span>{value}</span>
                )
            }
        },
        {
            key: 'ecuEngineOdometer',
            show: true,
            title: messages['raw_data_tab_ecu_engine_odometer'],
            width: 200,
            export: true,
            resize: true,
            functionString: `
                let val = row?.ecuEngineOdometer ?? 0;
                return Math.floor(val * 100) / 100;
            `,
            code: "raw_data_tab_ecu_engine_odometer",
            field: "ecuEngineOdometer",
            render: (dataItem) => {
                let val = dataItem?.ecuEngineOdometer ?? 0;
                let ecuEngineOdometer = Math.floor(val * 100) / 100;
                return (
                    <span>{ecuEngineOdometer}</span>
                )
            }
        },
        {
            key: 'ecuEngineOdometer_off',
            show: true,
            title: messages['raw_data_tab_ecu_engineOdometer_off'],
            width: 250,
            export: true,
            resize: true,
            functionString: `
                let offsetEcuEngineOdometer = ${device?.offsetEcuEngineOdometer || 0};
                let val = row?.ecuEngineOdometer ?? 0;
                return Math.floor((val + offsetEcuEngineOdometer) * 100) / 100;
            `,
            code: "raw_data_tab_ecu_engineOdometer_off",
            render: (dataItem) => {
                let offsetEcuEngineOdometer = device?.offsetEcuEngineOdometer || 0;
                let val = dataItem?.ecuEngineOdometer ?? 0;
                let value = Math.floor((val + offsetEcuEngineOdometer) * 100) / 100;
                return (
                    <span>{value}</span>
                )
            }
        },
        {
            key: 'hDop',
            show: true,
            title: messages['raw_data_tab_hdop'],
            width: 100,
            field: "hDop",
            export: true,
            resize: true,
            functionString: 'return row?.hDop ? (row?.hDop > 0 ? (row?.hDop/100).toFixed(2): "0") : ""',
            code: "raw_data_tab_hdop",
            render: (item) => {
                if (item?.hDop) {
                    let hdop = getHDopColorLabel(item?.hDop, messages)
                    return <Chip title={hdop.value}
                        style={{
                            marginRight: 5,
                            backgroundColor: hdop.color,
                            color: '#ffff'
                        }}
                        label={hdop.label}
                    />
                }
                return <span>{""}</span>
            }
        },
        {
            key: 'vDop',
            show: false,
            title: messages['raw_data_tab_vdop'],
            width: 100,
            field: "vDop",
            export: true,
            resize: true,
            functionString: 'return row?.vDop',
            code: "raw_data_tab_vdop"
        },
        {
            key: 'rssi',
            show: true,
            title: messages['raw_data_tab_rssi'],
            width: 100,
            export: true,
            resize: true,
            functionString: `
            function formatRssi(data) {
                let jsonData = {}
                try{
                    jsonData = data ? JSON.parse(JSON.parse(data)) : null
                }catch(exc){}
                let rssi = jsonData?.rssi || "";
                return rssi;
              }
            return row?.deviceData ? formatRssi(row?.deviceData) : ''`,
            code: "raw_data_tab_rssi",
            render: (item) => {
                let jsonData = {}
                try {
                    jsonData = item?.deviceData ? JSON.parse(JSON.parse(item?.deviceData)) : null
                } catch (error) { }
                if (jsonData?.rssi)
                    return getRssiSignalIcon(jsonData?.rssi, messages)
                else
                    return <span>{""}</span>
            }
        },
        {
            key: 'batteryLevel',
            show: true,
            title: messages['raw_data_tab_battery_level'],
            width: 200,
            export: true,
            resize: true,
            functionString: 'return row?.deviceData ? JSON.parse(row?.deviceData).batteryPercent ? JSON.parse(row?.deviceData).batteryPercent: "" : ""',
            code: "raw_data_tab_battery_level",
            render: (dataItem) => {
                let battery = dataItem?.deviceData ? JSON.parse(dataItem.deviceData).batteryPercent ? JSON.parse(dataItem.deviceData).batteryPercent : null : null
                let color = "green"
                if (battery && battery < 10) {
                    color = "red"
                } else if (battery && battery < 20) {
                    color = "yellow"
                }
                return <span>{battery && <ProgressBar value={battery} color={color} />}</span>
            }
        },
        {
            key: 'rawData',
            show: false,
            title: messages['raw_data_tab_raw'],
            width: 300,
            field: "deviceData",
            export: true,
            resize: true,
            functionString: 'return row?.deviceData',
            code: "raw_data_tab_raw",
            render: (dataItem) => {
                let rawData = JSON.stringify(dataItem?.deviceData || {})
                return (
                    <>
                        {
                            rawData &&
                            <span>{truncateText(rawData, 200)}</span>
                        }
                    </ >
                )
            }
        }
    ])

    const current_week = moment().startOf('week');
    const tempStartDate = new Date(current_week.toString());
    const [startDate, setStartDate] = useState(tempStartDate);
    const [endDate, setEndDate] = useState(new Date());
    const [beforeRangeDate, setBeforeRangeDate] = useState({
        start: tempStartDate,
        end: endDate
    })
    const [last, setLast] = useState(filters?.last || { id: '20', name: '20' });
    const [rows, setRows] = useState(500);
    const [type, setType] = useState('last');

    const onChangeStartEndDate = ({ start, end }) => {
        const newStart = start && new Date(start);
        const newEnd = end && new Date(end);

        let fltrs = filters;
        fltrs.startDate = newStart;
        fltrs.endDate = newEnd;
        // Set New Date Range
        if (start && end) {
            const newDateRange = {
                start,
                end,
            };
            dispatch(rawDataTabSetBeforeRangeDate(newDateRange))
        }
        dispatch(rawDataTabSetFilters({ ...fltrs }))
    }

    const onCloseSelectDate = () => {
        const { start, end } = beforeRangeDateRed;
        if (start && end && !endDate) {
            let fltrs = filters;
            fltrs.startDate = start;
            fltrs.endDate = end;
            dispatch(rawDataTabSetFilters({ ...fltrs }))
        }
    }

    const onChange = (value) => {
        const { start, end } = value;
        onChangeStartEndDate({
            start,
            end
        })

    };

    const handleOrderType = (dateType) => {
        setDateType(dateType)
    }

    const applyRangeFilter = useCallback((dateType, startDate, endDate) => {
        return {
            fields: [dateType],
            GTE: moment.tz(moment(startDate).format("YYYY-MM-DD") + " 00:00:00", timezone)
                .utc().format("YYYY-MM-DDTHH:mm:ss.SSS") + "Z",
            LTE: moment.tz(moment(endDate).format("YYYY-MM-DD") + " 23:59:59", timezone)
                .utc().format("YYYY-MM-DDTHH:mm:ss.SSS") + "Z"
        }
    }, [timezone])

    const applyFilters = (paramType) => {
        let currentNewConditions = tableRef?.current?.conditions;
        let newType = paramType ? paramType : type
        // Remove existing deviceId, accountId and standalone range conditions to avoid duplicates
        currentNewConditions = currentNewConditions?.filter((item) => item?.fields?.[0] !== 'deviceId' && item?.fields?.[0] !== 'accountId' && !item?.range) || [];
        if (newType !== "last" && dateFilters.startDate && dateFilters.endDate) {
            currentNewConditions.push({
                fields: ["deviceId"],
                terms: [props?.id],
                exact: true,
                conditionOperator: "AND"
            });
            currentNewConditions.push({
                range: applyRangeFilter(dateType.id, dateFilters.startDate, dateFilters.endDate)
            });
        } else if (newType !== "last") {

            NotificationManager.warning(
                messages['raw_data_tab_date_range'],
                messages['raw_data_tab_raw_data_modal_title'],
                5000,
            );
            return
        } else {
            currentNewConditions.push({
                fields: ["deviceId"],
                terms: [props?.id],
                exact: true,
                conditionOperator: "AND"
            });
        }

        currentNewConditions.push({
            fields: ["accountId"],
            terms: [companyId],
            exact: true,
            conditionOperator: "AND"
        });

        let current = { key: dateType.id, order: "DESC" }

        if (dateType.id === "serverTime") {
            current = [{ field: "serverTime", order: "DESC" }, { field: "unitTime", order: "DESC" }]
        }

        dispatch(rawDataTabSetFilters({ ...dateFilters, last: last, type: newType }))
        //execute the account search
        tableRef?.current?.getData({
            newRows: newType === 'last' ? parseInt(last.id, 10) : rows || null,
            newSort: current,
            newConditions: currentNewConditions,
            showTotalCount: newType !== 'last',
        });


    }

    useEffect(() => {
        if (filters) {
            setDateFilters(filters)
            setType(filters.type)
            setLast(filters.last)

            setStartDate(filters.startDate)
            setEndDate(filters.endDate)

        }
    }, [filters])

    /**
     * Synchronizes date/last filters from DataView condition changes.
     */
    const handleChangeConditions = (tags, conditions, total, newColumns, rowsPeerPage, sort, _param7, isInitial) => {

        let currentConditions = {}
        currentConditions = conditions || [];
        let currentDateType = dateType.id
        let currentLast = last
        let currentType = type
        let currentDate = conditions.find((item) => item?.range?.fields)
        let fltrs = filters;
        if (currentDate) {
            currentDateType = currentDate?.range?.fields[0]
        }
        setDateType({ id: currentDateType, label: currentDateType === 'serverTime' ? messages['common_server_time'] : messages['common_unit_time'] })

        let currentSearchOption = conditions.find((item) => item?.range)

        currentConditions = currentConditions?.filter((item) => item?.fields?.[0] !== 'deviceId' && item?.fields?.[0] !== 'accountId' && !item?.range);

        if (currentConditions && currentSearchOption) {
            currentConditions && currentConditions.push({
                fields: ["deviceId"],
                terms: [props?.id],
                exact: true,
                conditionOperator: "AND"
            });
            currentConditions && currentConditions.push({
                range: applyRangeFilter(currentDateType, dateFilters.startDate, dateFilters.endDate)
            });
            setType('range')
            currentType = 'range'
            setRows(rows || 500)
            tableRef?.current.setRows(rows || 500)

        } else if (currentConditions && !currentSearchOption) {
            currentConditions && currentConditions.push({
                fields: ["deviceId"],
                terms: [props?.id],
                exact: true,
                conditionOperator: "AND"
            });
            setType("last")
            currentType = 'last'
        }
        if (rowsPeerPage && currentType === "last" && !isInitial) {
            setLast({ id: `${rowsPeerPage}`, name: `${rowsPeerPage}` })
            currentLast = { id: `${rowsPeerPage}`, name: `${rowsPeerPage}` }
        }
        let current = { key: currentDateType, order: "DESC" }
        if (currentDateType === "serverTime") {
            current = [{ field: "serverTime", order: "DESC" }, { field: "unitTime", order: "DESC" }]
        }

        tableRef?.current?.conditionsHandler(currentConditions);
        tableRef?.current.setSort(current)
        dispatch(rawDataTabSetFilters({ ...fltrs, last: currentLast, type: currentType, companyId: companyId }))

    }

    /**
     * Renders custom date/last filtering controls for raw data.
     */
    const renderExtraFilters = () => {
        return (
            <div style={{ width: "100%", display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                <Filters

                    onChangeFilters={() => applyFilters()}
                    startDate={startDate}
                    endDate={endDate}
                    setStartDate={setStartDate}
                    setEndDate={setEndDate}
                    setBeforeRangeDate={setBeforeRangeDate}
                    beforeRangeDate={beforeRangeDate}
                    onChange={onChange}
                    onChangeStartEndDate={onChangeStartEndDate}
                    onCloseSelectDate={onCloseSelectDate}
                    last={last}
                    setLast={setLast}
                    dateType={dateType}
                    handleOrderType={handleOrderType}
                    type={type}
                    setType={(value) => {
                        setType(value)
                        if (value === 'range') {
                            tableRef?.current?.setRows(RANGE_MODE_ROWS);
                        }
                        dispatch(clearRedux("COMMON", {
                            'device-readings-vidfleet': {}
                        }))
                        applyFilters(value)
                    }}
                />
            </div>
        )
    }

    const escapeCsvValue = (val) => {
        const str = val === undefined || val === null ? '' : String(val);
        return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
    };

    const evaluateFunctionString = (functionString, row) => {
        if (!functionString) return '';
        try {
            return new Function('row', 'moment', functionString)(row, moment);
        } catch (error) {
            return '';
        }
    };

    const buildDynamicFields = (columnsToExport, baseQuery, includeHidden = false) => {
        let params = baseQuery;
        const columnsToUse = includeHidden
            ? columnsToExport.filter((item) => item.export)
            : columnsToExport.filter((item) => item.show);

        columnsToUse.map((column) => {
            params += " "
            let fields = column.field && column.field.split(",") || []
            fields.map(f => {
                let array = f.split(".")
                if (array.length > 1) {
                    let text = "";
                    let closeText = "";
                    if (params.includes(array[0])) {
                        let divider = params.split(`${array[0]} { `)
                        params = divider[0] + ` ${array[0]} { ` + `${array[1]} ` + divider[1]
                    } else {
                        for (let i = 0; i < array.length; i++) {
                            if (!params.includes(array[0])) {
                                text += array[i]
                            }
                            if (i < array.length - 1) {
                                text += " { "
                                closeText += " } "
                            }
                        }
                    }
                    params += text + closeText
                }
                else params += array[0]
            })
        })
        return params;
    };

    const buildGraphQLQuery = ({ functionName, conditionsType, sortType, fields, extraFields = '' }) => {
        const sortParam = sortType ? `, $sort: [${sortType}]` : '';
        const sortArg = sortType ? ', sort: $sort' : '';
        return `
            query ${functionName} ($conditions: [${conditionsType}]${sortParam}, $limit: Int, $offset: Int) {
                result: ${functionName} (conditions: $conditions${sortArg}, limit: $limit, offset: $offset) {
                    items {
                        ${fields}
                    }
                    ${extraFields}
                }
            }
        `;
    };

    const generateCsvFromRows = (rows, columnsVisibility) => {
        const exportColumns = (columns || []).filter((col) =>
            columnsVisibility === 'All' ? col.export : col.export && col.show
        );

        const headerRow = exportColumns.map((col) => col.title || col.field);
        const csvLines = [headerRow.map(escapeCsvValue).join(',')];

        rows.forEach((row) => {
            const line = exportColumns.map((col) =>
                escapeCsvValue(evaluateFunctionString(col.functionString, row))
            );
            csvLines.push(line.join(','));
        });

        const csvContent = csvLines.join('\n');
        const date = new Date();
        const fileName = `rawdata_${date.getFullYear()}${date.getMonth()}${date.getDay()}${date.getHours()}${date.getMinutes()}.csv`;

        const a = document.createElement('a');
        a.href = URL.createObjectURL(new Blob([csvContent], { type: 'text/csv;encoding:utf-8' }));
        a.setAttribute('download', fileName);
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);

        NotificationManager.success(`Exported ${rows.length} rows`, 'Export Complete', 3000);
    };

    const exportLocalCsv = async (currentType, columnsVisibility) => {
        NotificationManager.info('Generating export...', 'Export Started', 2000);
        let rowsToExport = [];

        if (columnsVisibility === 'All') {
            try {
                // Fetch all fields needed for export columns from backend
                const baseQuery = "id deviceId deviceData";
                const dynamicFields = buildDynamicFields(columns, baseQuery, true);

                const query = buildGraphQLQuery({
                    functionName: 'searchDeviceReadings',
                    conditionsType: 'DeviceReadingSearchInput',
                    sortType: 'DeviceReadingSortInput',
                    fields: dynamicFields,
                    extraFields: 'total',
                });

                const sanitizedConditions = (tableRef?.current?.conditions || [])
                    ?.map(({ tags, account, ...condition }) => condition) || [];

                const variables = {
                    limit: entityState?.items?.length || 500,
                    offset: 0,
                    sort: [{ field: dateType.id, order: "DESC" }],
                    conditions: sanitizedConditions,
                };

                const response = await clientQuery(query, variables, endpoints.GRAPHQL_GENERAL);
                rowsToExport = response?.result?.items || [];
            } catch (error) {
                console.error('Error fetching complete data for export:', error);
                NotificationManager.error('Error exporting data', 'Export Failed', 5000);
                return;
            }
        } else {
            // For visible columns, use already loaded data
            rowsToExport = entityState?.items || [];
        }

        if (rowsToExport.length === 0) {
            NotificationManager.warning('No data to export', 'Export Warning', 5000);
            return;
        }

        generateCsvFromRows(rowsToExport, columnsVisibility);
    };

    return (
        <div className="raw-data-vidfleet-tab" style={{ marginTop: 10 }}>
            <DataViewInfiniteScroll
                ref={tableRef}
                graphqlConfig={{
                    function: "searchDeviceReadings",
                    sortType: "DeviceReadingSortInput",
                    conditionsType: "DeviceReadingSearchInput",
                    customQuery: "id deviceId deviceData",
                    dynamicQuery: true,
                }}
                searchConfig={{
                    disabled: true,
                }}
                uiConfig={{
                    hideFilters: true,
                    hideSplit: true,
                    hidePagination: type === "last",
                    topHeight: props?.single ? 286 : 200,
                    height: height && height + "px" || "500px",
                    buttonsFlexWrap: false
                }}
                selectionConfig={{
                    disabled: true,
                }}
                exportConfig={{
                    fileName: "rawdata",
                    disablePDF: true,
                    generate: type === 'last' ? exportLocalCsv : undefined,
                }}
                defaultsConfig={{
                    sort: { field: dateType?.id, order: "DESC" },
                    rowsPerPage: 500,
                    valuesPerPage: valuesPerPage,
                }}
                virtualizationConfig={{
                    overscan: 100,
                }}
                name="raw-data-vidfleet-v10"
                entity="device-readings-vidfleet"
                columns={columns}
                messages={messages}
                onChangeConditions={handleChangeConditions}
                extraFilters={renderExtraFilters}
                onChangeColumns={(items) => setColumns(items)}
                tableButtons={["export", 'orderColumn']}
                infiniteScroll={type !== 'last'}
                showTotalCount={type !== 'last'}
                onChangeRowsPerPage={(rows) => {
                    setRows(rows)
                    tableRef?.current?.getData({ newRows: rows });
                }}
            />
            {openModal && <DeviceReadingInfo />}
        </div>
    )
}

export default VidFleetView;
